import { useState, useEffect, useCallback } from "react";
import { Link, useOutletContext } from "react-router-dom";
import {
  SubscriptionItem,
  SubscriptionCreate,
  listSubscriptions,
  createSubscription,
  updateSubscription,
  deleteSubscription,
  runSubscription,
} from "../../../services/subscription";
import SubscriptionModal from "./components/SubscriptionModal";
import { ensurePermission } from "../../../utils/permissions";
import { formatBeijingDateTime } from "../../../utils/datetime";
import RIcon from "../../../components/RIcon";

const SCHEDULE_LABELS: Record<string, string> = {
  once: "单次执行",
  daily: "每日定时",
  weekly: "每周定时",
  custom: "自定义 Cron",
};

const WEEKDAY_MAP: Record<number, string> = { 1: "周一", 2: "周二", 3: "周三", 4: "周四", 5: "周五", 6: "周六", 7: "周日" };

type SubStatus = "active" | "paused" | "error";

const STATUS_CONFIG: Record<SubStatus, { label: string; cls: string; dot: string }> = {
  active: { label: "运行中", cls: "bg-emerald-50 text-emerald-600", dot: "bg-emerald-500" },
  paused: { label: "已暂停", cls: "bg-gray-100 text-[#718096]", dot: "bg-gray-400" },
  error: { label: "异常", cls: "bg-red-50 text-red-500", dot: "bg-red-500" },
};

function getStatusConfig(status: string) {
  return STATUS_CONFIG[status as SubStatus] ?? STATUS_CONFIG.paused;
}

export default function ContentSubPage() {
  const { user } = useOutletContext<{ user: import("../../../services/auth").UserInfo | null }>();
  const [list, setList] = useState<SubscriptionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<SubscriptionItem | null>(null);
  const [toast, setToast] = useState("");
  const [runningId, setRunningId] = useState<number | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2800);
  };

  const loadList = useCallback(async () => {
    try {
      const data = await listSubscriptions();
      setList(data);
    } catch {
      showToast("加载订阅列表失败");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const handleSave = async (data: SubscriptionCreate) => {
    if (!ensurePermission(user, "subscriptions:write", showToast)) return;
    try {
      if (editTarget) {
        await updateSubscription(editTarget.id, data);
        showToast("订阅已更新");
      } else {
        await createSubscription(data);
        showToast("订阅已创建，即将开始执行");
      }
      setModalOpen(false);
      setEditTarget(null);
      await loadList();
    } catch (err) {
      showToast(err instanceof Error ? err.message : (editTarget ? "更新订阅失败" : "创建订阅失败"));
    }
  };

  const handleDelete = async (id: number) => {
    if (!ensurePermission(user, "subscriptions:write", showToast)) return;
    try {
      await deleteSubscription(id);
      setList(prev => prev.filter(s => s.id !== id));
      showToast("订阅已删除");
    } catch {
      showToast("删除订阅失败");
    }
  };

  const handleToggle = async (sub: SubscriptionItem) => {
    if (!ensurePermission(user, "subscriptions:write", showToast)) return;
    const newStatus = sub.status === "active" ? "paused" : "active";
    try {
      await updateSubscription(sub.id, { status: newStatus });
      // 后端 PUT 只返回 { message, id } 而不是完整对象，这里用"本地字段合并"避免整项被置空导致页面白屏
      setList(prev => prev.map(s => s.id === sub.id ? { ...s, status: newStatus } : s));
      showToast(newStatus === "active" ? "订阅已启用" : "订阅已暂停");
    } catch {
      showToast("状态切换失败");
    }
  };

  const handleRun = async (id: number) => {
    if (!ensurePermission(user, "subscriptions:write", showToast)) return;
    setRunningId(id);
    showToast("任务已触发，正在采集中...");
    try {
      await runSubscription(id);
      let attempts = 0;
      while (attempts < 20) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const data = await listSubscriptions();
        setList(data);
        const current = data.find((item) => item.id === id);
        if (!current) break;
        if ((current.last_run_at && current.report_count > (list.find((item) => item.id === id)?.report_count || 0)) || current.status === "error") {
          break;
        }
        attempts += 1;
      }
      setRunningId(null);
      showToast("采集流程已结束，请到报告列表查看结果");
    } catch (err) {
      setRunningId(null);
      showToast(err instanceof Error ? err.message : "触发任务失败");
    }
  };

  const activeCount = list.filter(s => s.status === "active").length;
  const totalReports = list.reduce((sum, s) => sum + s.report_count, 0);

  return (
    <div className="p-6 space-y-6 min-h-full bg-gray-50/40">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#1A2B3C]">内容订阅</h1>
          <p className="text-sm text-[#718096] mt-0.5">自动采集指定网站的关键词内容，定时生成情报报告</p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/workbench/content-sub/reports"
            className="flex items-center gap-1.5 px-4 h-9 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center"><RIcon name="file-list-3-line" /></div>
            查看报告
          </Link>
          <button
            onClick={() => { if (ensurePermission(user, "subscriptions:write", showToast)) { setEditTarget(null); setModalOpen(true); } }}
            className="flex items-center gap-1.5 px-4 h-9 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center"><RIcon name="add-line" /></div>
            新建订阅
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {[
          { label: "订阅总数", value: list.length, icon: "ri-rss-line", color: "text-[#0EA5A0] bg-[#0EA5A0]/10" },
          { label: "运行中", value: activeCount, icon: "ri-play-circle-line", color: "text-emerald-600 bg-emerald-50" },
          { label: "累计报告", value: totalReports, icon: "ri-file-chart-line", color: "text-violet-600 bg-violet-50" },
          { label: "覆盖网站", value: [...new Set(list.flatMap(s => s.websites))].length, icon: "ri-global-line", color: "text-amber-600 bg-amber-50" },
        ].map(stat => (
          <div key={stat.label} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.color}`}>
              <i className={`${stat.icon} text-lg`} />
            </div>
            <div>
              <div className="text-xl font-bold text-[#1A2B3C]">{stat.value}</div>
              <div className="text-xs text-[#718096]">{stat.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Subscription Cards */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-100 py-20 text-center">
          <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 bg-gray-100 rounded-full">
            <RIcon name="loader-4-line" className="text-2xl text-[#718096] animate-spin" />
          </div>
          <p className="text-[#718096] text-sm">加载中...</p>
        </div>
      ) : list.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 py-20 text-center">
          <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 bg-gray-100 rounded-full">
            <RIcon name="rss-line" className="text-2xl text-[#718096]" />
          </div>
          <p className="text-[#4A5568] font-medium">暂无订阅</p>
          <p className="text-sm text-[#718096] mt-1">点击「新建订阅」开始配置第一个内容监控任务</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {list.map(sub => {
            const status = getStatusConfig(sub.status);
            const isRunning = runningId === sub.id;
            return (
              <div key={sub.id} className="bg-white rounded-xl border border-gray-100 p-5 flex flex-col gap-4">
                {/* Card Header */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold text-[#1A2B3C]">{sub.name}</h3>
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${status.cls}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                        {status.label}
                      </span>
                    </div>
                    {sub.description && <p className="text-xs text-[#718096] mt-0.5 truncate">{sub.description}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => handleToggle(sub)}
                      title={sub.status === "active" ? "暂停" : "启用"}
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer text-[#718096]"
                    >
                      <i className={sub.status === "active" ? "ri-pause-circle-line" : "ri-play-circle-line"} />
                    </button>
                    <button
                      onClick={() => { if (ensurePermission(user, "subscriptions:write", showToast)) { setEditTarget(sub); setModalOpen(true); } }}
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer text-[#718096]"
                    >
                      <RIcon name="edit-line" />
                    </button>
                    <button
                      onClick={() => handleDelete(sub.id)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 cursor-pointer text-[#718096] hover:text-red-500"
                    >
                      <RIcon name="delete-bin-line" />
                    </button>
                  </div>
                </div>

                {/* Websites */}
                <div>
                  <p className="text-xs text-[#718096] mb-1.5">目标网站</p>
                  <div className="flex flex-wrap gap-1.5">
                    {sub.websites.slice(0, 4).map(w => (
                      <span key={w} className="text-xs bg-gray-100 text-[#4A5568] px-2 py-0.5 rounded-md flex items-center gap-1 max-w-48 overflow-hidden" title={w}>
                        <div className="w-3 h-3 flex items-center justify-center flex-shrink-0"><RIcon name="global-line" /></div>
                        <span className="truncate">{w}</span>
                      </span>
                    ))}
                    {sub.websites.length > 4 && <span className="text-xs text-[#718096]">+{sub.websites.length - 4} 个</span>}
                  </div>
                </div>

                {/* Keywords */}
                <div>
                  <p className="text-xs text-[#718096] mb-1.5">关键词</p>
                  <div className="flex flex-wrap gap-1.5">
                    {sub.keywords.slice(0, 5).map(kw => (
                      <span key={kw} className="text-xs bg-[#0EA5A0]/10 text-[#0EA5A0] px-2.5 py-0.5 rounded-full">{kw}</span>
                    ))}
                    {sub.keywords.length > 5 && <span className="text-xs text-[#718096]">+{sub.keywords.length - 5}</span>}
                  </div>
                </div>

                {/* Meta row */}
                <div className="grid grid-cols-2 gap-2 text-xs text-[#718096] pt-1 border-t border-gray-50">
                  <div className="flex items-center gap-1">
                    <div className="w-3.5 h-3.5 flex items-center justify-center"><RIcon name="time-line" /></div>
                    {SCHEDULE_LABELS[sub.schedule_type] ?? sub.schedule_type}
                    {sub.schedule_type === "daily" && sub.schedule_time && ` ${sub.schedule_time}`}
                    {sub.schedule_type === "weekly" && sub.schedule_day_of_week && ` ${WEEKDAY_MAP[sub.schedule_day_of_week]}`}
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3.5 h-3.5 flex items-center justify-center"><RIcon name="file-chart-line" /></div>
                    {sub.report_count} 份报告
                  </div>
                </div>

                {/* Footer actions */}
                <div className="flex items-center justify-between pt-1">
                  <span className="text-xs text-[#718096]">
                    {sub.last_run_at ? `上次执行：${formatBeijingDateTime(sub.last_run_at)}` : "尚未执行"}
                  </span>
                  <div className="flex gap-2">
                    <Link
                      to={`/workbench/content-sub/reports?sub=${sub.id}`}
                      className="text-xs px-3 h-7 border border-gray-200 text-[#4A5568] rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap flex items-center"
                    >
                      查看报告
                    </Link>
                    <button
                      onClick={() => handleRun(sub.id)}
                      disabled={isRunning || sub.status === "paused"}
                      className="text-xs px-3 h-7 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg cursor-pointer whitespace-nowrap flex items-center gap-1 disabled:opacity-50"
                    >
                      {isRunning ? (
                        <><div className="w-3 h-3 flex items-center justify-center"><RIcon name="loader-4-line" className="animate-spin" /></div>执行中</>
                      ) : (
                        <><div className="w-3 h-3 flex items-center justify-center"><RIcon name="play-line" /></div>立即执行</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-[#1A2B3C] text-white text-sm px-5 py-2.5 rounded-xl z-50">
          {toast}
        </div>
      )}

      {modalOpen && (
        <SubscriptionModal
          initial={editTarget}
          onClose={() => { setModalOpen(false); setEditTarget(null); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
